<template>
    <v-data-table
        class="elevation-0"
        :headers="headers"
        :items="items"
        :items-per-page="15"
        :footer-props="dataTableFooterProps"
        :search="dataTableSearch"
        :custom-filter="dataTableCustomFilter"
        :loading="isBusy"
        :loading-text="$t('common.loading_table_data')"
    >
        <template v-slot:item.default="{ item }">
            {{ item.default ? $t('common.yes') : $t('common.no') }}
        </template>
        <template v-slot:item.actions="{ item }">
            <span class="text-no-wrap">
                <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                        <v-icon small @click="onUpdate(item)" v-on="on" class="mr-2">
                            mdi-pencil
                        </v-icon>
                    </template>
                    <span>{{ $t('tooltip.buyer_address.update') }}</span>
                </v-tooltip>
                <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                        <v-icon small @click="onDelete(item)" v-on="on">
                            mdi-delete
                        </v-icon>
                    </template>
                    <span>{{ $t('tooltip.buyer_address.delete') }}</span>
                </v-tooltip>
            </span>
        </template>
        <template v-slot:top>
            <v-toolbar flat color="white">
                <v-text-field
                    v-model="dataTableSearch"
                    prepend-inner-icon="mdi-magnify"
                    :placeholder="$t('common.filter')"
                    outlined
                    dense
                    hide-details
                />
                <v-spacer></v-spacer>
                <v-dialog v-model="dialog" max-width="800">
                    <template v-slot:activator="{ on }">
                        <v-btn color="success"
                               dark
                               class="mb-2"
                               v-on="on"
                               @click="cleanEditedItem"
                        >
                            {{ $t('button.new_buyer_address') }}
                        </v-btn>
                    </template>
                    <v-card :loading="isBusy">
                        <v-card-title>
                            <span class="headline">{{ dialogTitle }}</span>
                            <v-spacer/>
                            <v-btn @click="dialog = false" icon>
                                <v-icon>mdi-window-close</v-icon>
                            </v-btn>
                        </v-card-title>
                        <v-card-text>
                            <v-container>
                                <v-row>
                                    <v-col cols="12">
                                        <v-switch
                                            v-model="$v.editedItem.default.$model"
                                            :label="$t('address.default')"
                                            :error-messages="defaultErrors"
                                            @input="$v.editedItem.default.$touch()"
                                            @blur="$v.editedItem.default.$touch()"
                                        />
                                    </v-col>
                                    <v-col cols="12" md="4">
                                        <v-text-field
                                            v-model="$v.editedItem.address.street.$model"
                                            :label="$t('address.street')"
                                            :error-messages="streetErrors"
                                            @input="$v.editedItem.address.street.$touch()"
                                            @blur="$v.editedItem.address.street.$touch()"
                                        />
                                    </v-col>
                                    <v-col cols="12" md="4">
                                        <v-text-field
                                            v-model="$v.editedItem.address.house_number.$model"
                                            :label="$t('address.house_number')"
                                            :error-messages="houseNumberErrors"
                                            @input="$v.editedItem.address.house_number.$touch()"
                                            @blur="$v.editedItem.address.house_number.$touch()"
                                        />
                                    </v-col>
                                    <v-col cols="12" md="4">
                                        <v-text-field
                                            v-model="$v.editedItem.address.suburb.$model"
                                            :label="$t('address.suburb')"
                                            :error-messages="suburbErrors"
                                            @input="$v.editedItem.address.suburb.$touch()"
                                            @blur="$v.editedItem.address.suburb.$touch()"
                                        />
                                    </v-col>
                                    <v-col cols="12" md="4">
                                        <v-text-field
                                            v-model="$v.editedItem.address.city.$model"
                                            :label="$t('address.city')"
                                            :error-messages="cityErrors"
                                            @input="$v.editedItem.address.city.$touch()"
                                            @blur="$v.editedItem.address.city.$touch()"
                                        />
                                    </v-col>
                                    <v-col cols="12" md="4">
                                        <v-text-field
                                            v-model="$v.editedItem.address.postal_code.$model"
                                            :label="$t('address.postal_code')"
                                            :error-messages="postalCodeErrors"
                                            @input="$v.editedItem.address.postal_code.$touch()"
                                            @blur="$v.editedItem.address.postal_code.$touch()"
                                        />
                                    </v-col>
                                    <v-col cols="12" md="4">
                                        <v-text-field
                                            v-model="$v.editedItem.address.country.$model"
                                            :label="$t('address.country')"
                                            :error-messages="countryErrors"
                                            @input="$v.editedItem.address.country.$touch()"
                                            @blur="$v.editedItem.address.country.$touch()"
                                        />
                                    </v-col>
                                    <v-col cols="12" md="4">
                                        <v-select
                                            v-model="$v.editedItem.address.country_iso_code.$model"
                                            :items="countryIsoCodes"
                                            :label="$t('address.country_iso_code')"
                                            :error-messages="countryIsoCodeErrors"
                                            @input="$v.editedItem.address.country_iso_code.$touch()"
                                            @blur="$v.editedItem.address.country_iso_code.$touch()"
                                            class="required"
                                            required
                                        />
                                    </v-col>
                                </v-row>
                            </v-container>
                        </v-card-text>
                        <v-card-actions>
                            <v-spacer></v-spacer>
                            <v-btn color="blue darken-1" text @click="onClose" :disabled="isBusy">
                                {{ $t('button.close') }}
                            </v-btn>
                            <v-btn color="blue darken-1" text @click="onSave" :disabled="isBusy">
                                {{ $t('button.save') }}
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-dialog>
            </v-toolbar>
        </template>
    </v-data-table>
</template>

<script>
import {API} from "@/api/api";
import SnackbarMixin from "@/mixins/SnackbarMixin";
import DataTableMixin from "@/mixins/DataTableMixin";
import {required} from "vuelidate/lib/validators";
import {validationMixin} from "vuelidate";
import {countries} from "@/enum/countries";

export default {
    name: "BuyerAddressesTable",
    mixins: [SnackbarMixin, DataTableMixin, validationMixin],
    props: {
        warehouseId: {
            type: Number,
            required: true
        },
        buyerId: {
            type: Number,
            required: true
        }
    },
    data: () => ({
        // loading
        isBusyAddresses: false,
        isBusyCreate: false,
        isBusyUpdate: false,
        isBusyDelete: false,
        // table data
        items: [],
        // create/update dialog
        dialog: false,
        // edited user
        editedItem: {
            default: null,
            address: {
                street: null,
                house_number: null,
                suburb: null,
                city: null,
                postal_code: null,
                country: null,
                country_iso_code: null
            }
        },
        defaultItem: {
            default: null,
            address: {
                street: null,
                house_number: null,
                suburb: null,
                city: null,
                postal_code: null,
                country: null,
                country_iso_code: null
            }
        },
        editedIndex: -1,
    }),
    validations: {
        editedItem: {
            default: {},
            address: {
                street: {},
                house_number: {},
                suburb: {},
                city: {},
                postal_code: {},
                country: {},
                country_iso_code: {required}
            }
        }
    },
    computed: {
        isBusy() {
            return this.isBusyAddresses || this.isBusyCreate || this.isBusyUpdate || this.isBusyDelete;
        },
        dialogTitle() {
            return this.editedIndex === -1 ? this.$t('buyers.new_buyer_address') : this.$t('buyers.edit_buyer_address')
        },
        countryIsoCodes() {
            return countries.map(country => ({
                value: country.code,
                text: country.name + " (" + country.code + ")"
            }));
        },
        headers() {
            return [
                {
                    text: '#',
                    value: 'address.external_id'
                },
                {
                    text: this.$t('address.default'),
                    value: 'default'
                },
                {
                    text: this.$t('address.street'),
                    value: 'address.street'
                },
                {
                    text: this.$t('address.house_number'),
                    value: 'address.house_number'
                },
                {
                    text: this.$t('address.suburb'),
                    value: 'address.suburb'
                },
                {
                    text: this.$t('address.city'),
                    value: 'address.city'
                },
                {
                    text: this.$t('address.postal_code'),
                    value: 'address.postal_code'
                },
                {
                    text: this.$t('address.country'),
                    value: 'address.country'
                },
                {
                    text: this.$t('address.country_iso_code'),
                    value: 'address.country_iso_code'
                },
                {
                    text: this.$t('address.phone'),
                    value: 'address.phone'
                },
                {
                    text: this.$t('address.email'),
                    value: 'address.email'
                },
                {
                    text: '',
                    value: 'actions',
                    sortable: false,
                    align: 'end'
                }
            ];
        },
        defaultErrors() {
            return [];
        },
        streetErrors() {
            return [];
        },
        houseNumberErrors() {
            return [];
        },
        suburbErrors() {
            return [];
        },
        cityErrors() {
            return [];
        },
        postalCodeErrors() {
            return [];
        },
        countryErrors() {
            return [];
        },
        countryIsoCodeErrors() {
            const errors = [];
            if (!this.$v.editedItem.address.country_iso_code.$dirty) return errors;
            !this.$v.editedItem.address.country_iso_code.required && errors.push(this.$t('validation.country_iso_code.required'));
            return errors;
        },
    },
    methods: {
        onUpdate(address) {
            this.editedIndex = this.items.indexOf(address);
            this.editedItem = Object.assign({}, address);
            this.dialog = true;
        },
        onDelete(address) {
            if (!confirm(this.$t('confirm.delete_address'))) {
                return;
            }

            this.isBusyDelete = true;
            API.delete('/api/buyers/' + encodeURI(this.buyerId) + '/addresses/' + encodeURI(address.address.external_id))
                .then(() => {
                    this.snackbarSuccess(this.$t('snackbar.address.deleted'));
                    this.fetchAddresses();
                })
                .catch(error => {
                    this.snackbarError(error);
                })
                .finally(() => {
                    this.isBusyDelete = false;
                });
        },
        onSave() {
            this.$v.editedItem.$touch();
            if (this.$v.editedItem.$anyError) {
                return;
            }

            if (this.editedIndex > -1) {
                // update
                this.isBusyUpdate = true;
                API.patch('/api/buyers/' + encodeURI(this.buyerId) + '/addresses/' + encodeURI(this.items[this.editedIndex].address.external_id), this.editedItem)
                    .then(() => {
                        this.onClose();
                        this.snackbarSuccess(this.$t('snackbar.address.updated'));
                        this.fetchAddresses();
                    })
                    .catch(error => {
                        this.snackbarError(error);
                    })
                    .finally(() => {
                        this.isBusyUpdate = false;
                    })
            } else {
                // create
                this.isBusyCreate = true;
                API.post('/api/buyers/' + encodeURI(this.buyerId) + '/addresses', {
                    ...this.editedItem,
                    warehouse: this.warehouseId
                })
                    .then(() => {
                        this.onClose();
                        this.snackbarSuccess(this.$t('snackbar.address.created'));
                        this.fetchAddresses();
                    })
                    .catch(error => {
                        this.snackbarError(error);
                    })
                    .finally(() => {
                        this.isBusyCreate = false;
                    });
            }
        },
        onClose() {
            this.dialog = false;
            setTimeout(() => {
                this.cleanEditedItem();
            }, 300);
        },
        fetchAddresses() {
            this.isBusyAddresses = true;
            API.get('/api/addresses/by-buyer/' + encodeURI(this.buyerId))
                .then(response => {
                    this.items = response.data;
                })
                .catch(error => {
                    this.snackbarError(error);
                })
                .finally(() => {
                    this.isBusyAddresses = false;
                });
        },
        cleanEditedItem() {
            this.$v.editedItem.$reset();
            this.editedItem = Object.assign({}, this.defaultItem);
            this.editedIndex = -1;
        }
    },
    created() {
        this.fetchAddresses();
    }
}
</script>

<style lang="scss">
div.v-input.required {
    label:after {
        content: " *";
        color: red;
    }
}
</style>
